import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { ecommerceService } from '../../services/ecommerceService';

// Async thunks for order operations
export const fetchOrders = createAsyncThunk(
  'ecommerceOrders/fetchOrders',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await ecommerceService.getOrders(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchOrderDetail = createAsyncThunk(
  'ecommerceOrders/fetchOrderDetail',
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await ecommerceService.getOrder(orderId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const trackOrder = createAsyncThunk(
  'ecommerceOrders/trackOrder',
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await ecommerceService.trackOrder(orderId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const cancelOrder = createAsyncThunk(
  'ecommerceOrders/cancelOrder',
  async ({ orderId, reason }, { rejectWithValue }) => {
    try {
      const response = await ecommerceService.cancelOrder(orderId, reason);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createOrder = createAsyncThunk(
  'ecommerceOrders/createOrder',
  async (orderData, { rejectWithValue }) => {
    try {
      const response = await ecommerceService.createOrder(orderData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const verifyPayment = createAsyncThunk(
  'ecommerceOrders/verifyPayment',
  async ({ paymentId, gatewayResponse }, { rejectWithValue }) => {
    try {
      const response = await ecommerceService.verifyPayment(paymentId, gatewayResponse);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const initialState = {
  orders: [],
  orderDetail: null,
  trackingInfo: null,
  recentOrder: null,
  pagination: {
    count: 0,
    next: null,
    previous: null,
  },
  loading: false,
  detailLoading: false,
  trackingLoading: false,
  createOrderLoading: false,
  error: null,
  successMessage: null,
};

const ecommerceOrderSlice = createSlice({
  name: 'ecommerceOrders',
  initialState,
  reducers: {
    clearOrderDetail: (state) => {
      state.orderDetail = null;
    },
    clearTrackingInfo: (state) => {
      state.trackingInfo = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    clearRecentOrder: (state) => {
      state.recentOrder = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Orders
      .addCase(fetchOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = action.payload.results || action.payload;
        state.pagination = {
          count: action.payload.count || 0,
          next: action.payload.next || null,
          previous: action.payload.previous || null,
        };
      })
      .addCase(fetchOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch Order Detail
      .addCase(fetchOrderDetail.pending, (state) => {
        state.detailLoading = true;
        state.error = null;
      })
      .addCase(fetchOrderDetail.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.orderDetail = action.payload;
      })
      .addCase(fetchOrderDetail.rejected, (state, action) => {
        state.detailLoading = false;
        state.error = action.payload;
      })

      // Track Order
      .addCase(trackOrder.pending, (state) => {
        state.trackingLoading = true;
        state.error = null;
      })
      .addCase(trackOrder.fulfilled, (state, action) => {
        state.trackingLoading = false;
        state.trackingInfo = action.payload;
      })
      .addCase(trackOrder.rejected, (state, action) => {
        state.trackingLoading = false;
        state.error = action.payload;
      })

      // Cancel Order
      .addCase(cancelOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(cancelOrder.fulfilled, (state, action) => {
        state.loading = false;
        state.orderDetail = action.payload.order;
        state.successMessage = action.payload.message || 'Order cancelled successfully';
      })
      .addCase(cancelOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create Order
      .addCase(createOrder.pending, (state) => {
        state.createOrderLoading = true;
        state.error = null;
      })
      .addCase(createOrder.fulfilled, (state, action) => {
        state.createOrderLoading = false;
        state.recentOrder = action.payload;
        state.successMessage = action.payload.message || 'Order created successfully';
      })
      .addCase(createOrder.rejected, (state, action) => {
        state.createOrderLoading = false;
        state.error = action.payload;
      })

      // Verify Payment
      .addCase(verifyPayment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyPayment.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload.message || 'Payment verified successfully';
      })
      .addCase(verifyPayment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearOrderDetail,
  clearTrackingInfo,
  clearError,
  clearSuccessMessage,
  clearRecentOrder,
} = ecommerceOrderSlice.actions;

export default ecommerceOrderSlice.reducer;
